import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { MdCheck, MdMailOutline } from "react-icons/md";
import {
  CLEARS_PENDING_CODE,
  describeVerificationError,
  isEmailValid,
  normalizeEmail,
} from "./formsLogic";
import {
  ACCENT,
  DANGER,
  SUCCESS,
  fieldProps,
  invalidFieldProps,
} from "./formsTheme";
import {
  confirmFormEmailVerification,
  sendFormEmailVerification,
} from "../../../Backend/Forms/formsApi";

const secondsUntil = (iso) => {
  if (!iso) return 0;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return 0;
  return Math.max(0, Math.ceil((target - Date.now()) / 1000));
};

const useCountdown = (iso) => {
  const [seconds, setSeconds] = useState(() => secondsUntil(iso));
  useEffect(() => {
    setSeconds(secondsUntil(iso));
    if (!iso) return undefined;
    const handle = setInterval(() => {
      const next = secondsUntil(iso);
      setSeconds(next);
      if (next <= 0) clearInterval(handle);
    }, 1000);
    return () => clearInterval(handle);
  }, [iso]);
  return seconds;
};

const formatClock = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Email input for questions carrying `requireVerification`. The guest sends
 * a 6-digit code to the address they typed and confirms it before the form
 * can be submitted; editing a verified address voids the verification,
 * which the parent detects by comparing the answer to `verifiedEmails`.
 */
export const EmailVerifyInput = ({
  bookingId,
  formId,
  question,
  value,
  disabled,
  invalid,
  verified,
  onChange,
  onVerified,
}) => {
  const [phase, setPhase] = useState("idle");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [editing, setEditing] = useState(false);
  const sentEmailRef = useRef("");

  const resendIn = useCountdown(resendAvailableAt);
  const expiresIn = useCountdown(expiresAt);

  const email = String(value ?? "");
  const canSend = isEmailValid(email) && !disabled;

  // A pending code belongs to one address. If the guest edits the field the
  // code no longer applies, so fall back to the "send" state.
  useEffect(() => {
    if (phase !== "code") return;
    if (normalizeEmail(email) !== normalizeEmail(sentEmailRef.current)) {
      setPhase("idle");
      setCode("");
      setError(null);
      setExpiresAt(null);
    }
  }, [email, phase]);

  useEffect(() => {
    if (verified) {
      setPhase("idle");
      setCode("");
      setError(null);
      setEditing(false);
    }
  }, [verified]);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const res = await sendFormEmailVerification(bookingId, {
        formID: formId,
        questionID: question.id,
        email: email.trim(),
      });
      const data = res?.data || {};
      sentEmailRef.current = data.email || email.trim();
      setExpiresAt(data.expiresAt || null);
      setResendAvailableAt(data.resendAvailableAt || null);
      setPhase("code");
      setCode("");
    } catch (err) {
      setError(describeVerificationError(err));
      const payload = err?.response?.data || {};
      // A cooldown means a code is already outstanding — keep the guest on
      // the code entry step and respect the server's retry window.
      if (payload.code === "RESEND_COOLDOWN") {
        const retryIn = Number(payload.details?.retryInSeconds);
        if (Number.isFinite(retryIn) && retryIn > 0) {
          setResendAvailableAt(
            new Date(Date.now() + retryIn * 1000).toISOString()
          );
        }
        sentEmailRef.current = email.trim();
        setPhase("code");
      }
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    if (code.trim().length < 6 || confirming) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await confirmFormEmailVerification(bookingId, {
        formID: formId,
        questionID: question.id,
        email: email.trim(),
        code: code.trim(),
      });
      const data = res?.data || {};
      onVerified?.(data.email || email.trim());
    } catch (err) {
      setError(describeVerificationError(err));
      if (CLEARS_PENDING_CODE.has(err?.response?.data?.code)) {
        setPhase("idle");
        setCode("");
        setExpiresAt(null);
      }
    } finally {
      setConfirming(false);
    }
  };

  const locked = verified && !editing;

  return (
    <Stack spacing={2}>
      <Flex gap={2} align="flex-start">
        <Box flex="1" minW={0}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            placeholder={question.placeholder || "name@email.com"}
            isDisabled={disabled}
            isReadOnly={locked}
            h="46px"
            {...fieldProps}
            {...(invalid ? invalidFieldProps : {})}
            {...(verified
              ? { borderColor: "rgba(52, 211, 153, 0.5)" }
              : {})}
            onChange={(e) => onChange(e.target.value)}
          />
        </Box>
        {verified ? (
          <Button
            h="46px"
            px={3}
            borderRadius="12px"
            fontSize="12px"
            fontWeight={700}
            variant="ghost"
            color="whiteAlpha.700"
            isDisabled={disabled}
            _hover={{ bg: "whiteAlpha.100" }}
            onClick={() => setEditing(true)}
          >
            {editing ? "Editing" : "Change"}
          </Button>
        ) : (
          <Button
            h="46px"
            px={4}
            borderRadius="12px"
            fontSize="13px"
            fontWeight={700}
            bg={ACCENT}
            color="white"
            isDisabled={!canSend}
            isLoading={sending && phase === "idle"}
            leftIcon={<Icon as={MdMailOutline} fontSize="15px" />}
            _hover={{ bg: "#6e6be6" }}
            _active={{ bg: "#5c59d4" }}
            onClick={handleSend}
          >
            Verify
          </Button>
        )}
      </Flex>

      {verified && (
        <Flex align="center" gap={1.5}>
          <Badge
            bg="rgba(52, 211, 153, 0.15)"
            color={SUCCESS}
            border="1px solid rgba(52, 211, 153, 0.35)"
            fontSize="10px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.06em"
            px={2}
            py={0.5}
            borderRadius="full"
            display="inline-flex"
            alignItems="center"
            gap={1}
          >
            <Icon as={MdCheck} fontSize="11px" />
            Verified
          </Badge>
          {editing && (
            <Text fontSize="10px" color="gray.500">
              Changing this address means verifying it again.
            </Text>
          )}
        </Flex>
      )}

      {!verified && phase === "code" && (
        <Box
          p={3}
          borderRadius="12px"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <Text fontSize="11px" color="gray.400" mb={2}>
            We sent a 6-digit code to{" "}
            <Text as="span" color="white" fontWeight={600}>
              {sentEmailRef.current}
            </Text>
            . Enter it below to confirm.
          </Text>
          <Flex gap={2}>
            <Input
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConfirm();
              }}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              h="44px"
              letterSpacing="0.3em"
              fontWeight={700}
              textAlign="center"
              isDisabled={disabled}
              {...fieldProps}
            />
            <Button
              h="44px"
              px={4}
              borderRadius="12px"
              fontSize="13px"
              fontWeight={700}
              bg={ACCENT}
              color="white"
              isDisabled={code.length < 6 || disabled}
              isLoading={confirming}
              _hover={{ bg: "#6e6be6" }}
              _active={{ bg: "#5c59d4" }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Flex>
          <Flex align="center" justify="space-between" mt={2} gap={2}>
            <Text fontSize="10px" color="gray.500">
              {expiresIn > 0
                ? `Code expires in ${formatClock(expiresIn)}`
                : "This code may have expired."}
            </Text>
            <Button
              variant="link"
              fontSize="11px"
              fontWeight={700}
              color={resendIn > 0 ? "whiteAlpha.500" : ACCENT}
              isDisabled={resendIn > 0 || disabled}
              isLoading={sending}
              onClick={handleSend}
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
            </Button>
          </Flex>
        </Box>
      )}

      {error && (
        <Text fontSize="11px" color={DANGER}>
          {error}
        </Text>
      )}
    </Stack>
  );
};

export default EmailVerifyInput;
